import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Elements } from '@stripe/react-stripe-js';
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams
} from 'react-router-dom';
import { useNetwork } from '../../utils/network';
import { stripePromise } from '../../utils/stripe';
import {
  SUBSCRIPTION_DISCOUNT_PERCENT,
  isDiscountEligibleSubscriptionPackage
} from '../../utils/subscriptionDiscounts';
import { track } from '../../utils/track';
import SiteHeader from '../Home/DynamicHomepage/components/Landing/SiteHeader';
import RaffleFooter from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleFooter/RaffleFooter';
import SubscriptionUpgradeCards from '../Home/DynamicHomepage/components/NewLayout/RafflePage/SubscriptionUpgradeCards/SubscriptionUpgradeCards';
import SubscriptionStackingExplainer from '../Home/DynamicHomepage/components/NewLayout/RafflePage/SubscriptionStackingExplainer/SubscriptionStackingExplainer';
import { GiveawayCountdownTimer } from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleHero/GiveawayCountdownTimer';
import RaffleWinnersCarousel, {
  type WinnerItem
} from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleWinnersCarousel/RaffleWinnersCarousel';
import { YouTubeEmbed } from '../../utils/youtubeEmbed';
import { mapWinners, WINNER_VIDEO_URLS } from '../../utils/giveawayWinners';
import RaffleStripeCheckoutForm from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RafflePaymentPage/RaffleStripeCheckoutForm';
import RaffleEntryForm from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleForms/RaffleEntryForm';
import type { RafflePaymentPayload } from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RafflePaymentPage/RafflePaymentPage';
import type { PackItem } from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RafflePage';
import {
  toPackSubscriptionItems,
  byPriceAscending,
  type ApiPackage
} from '../../utils/packItems';
import {
  buildGiveawayOfferPaymentPayload,
  draftEntryPrefill,
  saveGiveawayOfferConfirmationDraft,
  displayedSubscriptionPrice,
  startingBonusEntries
} from './giveawayOfferPaymentPayload';
import {
  getGiveawayOfferDraft,
  saveGiveawayOfferDraft
} from './giveawayOfferFlowState';
import { useOfferSlug } from './giveawayOfferPages';
import { freeEntriesCopy } from './giveawayOfferEntries';
import { largestStartingBonus, offerPagePacks } from './giveawayOfferPacks';
import GiveawayOfferSocialLinks, { SocialTextLink } from './GiveawayOfferSocialLinks';
import GiveawayOfferMoreEntriesDialog from './GiveawayOfferMoreEntriesDialog';
import {
  PAGE_BG,
  CYAN,
  INK,
  BODY,
  MUTED,
  LINE,
  kickerSx,
  panelSx
} from './giveawayOfferTheme';

/* `/giveaway-offer/confirmation` — where a QR signup lands.
 *
 * THE ONE THING THIS PAGE MUST NOT DO is claim the entries are already counted.
 * They are not: `lib/initialGiveawayEntries.js` grants them when the member
 * clicks the verification link, deliberately, so a scanned poster cannot be
 * farmed with throwaway addresses. Every line of copy here is written to match
 * that — "reserved", "activate", never "you are entered".
 *
 * THE NUMBER COMES FROM THE SERVER (`GET /giveaway/offer?c=CODE`), not from a
 * constant in this bundle, because the same endpoint feeds the figure that is
 * actually granted — and it is THAT CODE'S figure: each poster code carries its
 * own amount. Hardcoding it here would give the promise and the grant two sources
 * of truth, and the first time an amount changed this page would advertise a
 * number nobody receives. The code arrives in this page's URL, put there by the
 * offer page; with no code there is no amount to promise, and none is printed.
 *
 * THE PACKS OPEN A CHECKOUT HERE. This is the funnel's THIRD guest-checkout
 * entry point (after `RafflePaymentPage` and `RafflePage`'s in-page subscription
 * dialog), and they share one contract — `RaffleStripeCheckoutForm` — so a change
 * to that form has to be re-verified on all three. It is built from the same
 * pieces as `PartnerCheckoutDialog` deliberately, so the comparison is easy.
 *
 * A PURCHASE REPLACES THE VERIFICATION CLICK. Buying a subscription grants the
 * free entries without the member setting a password, because a settled Stripe
 * invoice is a stronger anti-abuse signal than a clicked link. That decision is
 * enforced server-side ONLY, in the webhook's new-subscription branch — this page
 * cannot grant anything, and nothing it sends is trusted as proof of payment.
 */

type Offer = {
  entries: number;
  giveawayName: string | null;
  endDate: string | null;
  prizeAmount: number | null;
};

const processSteps = [
  {
    title: 'Complete the form',
    copy: 'Done — your details are in.'
  },
  {
    title: 'Confirm your account',
    copy: 'Check your inbox and click the link. This is what activates your entries.'
  },
  /* Where each thing happens, as the Partner funnel's steps say it: the live
     draw is WATCHED on Facebook, and Discord is the community — never the place
     the draw is shown. */
  {
    title: 'Join our Discord',
    copy: (
      <>
        Get involved with the INCRYPT community on{' '}
        <SocialTextLink network="discord" page="confirmation">
          Discord
        </SocialTextLink>
        .
      </>
    )
  },
  {
    title: 'Watch the live draw',
    copy: (
      <>
        The winner is drawn live on{' '}
        <SocialTextLink network="facebook" page="confirmation">
          Facebook
        </SocialTextLink>{' '}
        — make sure you are there.
      </>
    )
  }
];

const formatPrize = (amount: number | null) =>
  amount ? `A$${Number(amount).toLocaleString('en-AU')}` : 'the giveaway';

const ConfirmationPageContent = () => {
  const navigate = useNavigate();
  /* The path the funnel was opened on (`giveaway-offer`, `token2049-offer`, …),
   * so the subscription lands on that path's own `/subscribed`. */
  const offerSlug = useOfferSlug();
  /* The poster code the offer page sent along (`?c=`), read exactly as that page
   * reads it. In the URL, so a purged-and-reloaded phone tab still has it, and so
   * does the return from a Stripe redirect: RaffleStripeCheckoutForm's
   * return_url is this page's own URL. */
  const [searchParams] = useSearchParams();
  const offerCode = (searchParams.get('c') ?? '').trim().toUpperCase();
  const { get } = useNetwork();
  const theme = useTheme();
  /* The checkout dialog goes full-screen on a phone — the same rule the funnel's
   * other two checkouts use, and most QR scans arrive on one. */
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [offer, setOffer] = useState<Offer | null>(null);
  const [packs, setPacks] = useState<ApiPackage[]>([]);
  const [winners, setWinners] = useState<WinnerItem[]>([]);
  const [participants, setParticipants] = useState<number | null>(null);
  const [videoIndex, setVideoIndex] = useState(0);

  /* Fetch once — same fix, same reason as the offer page.
   *
   * The previous useCallback([get]) + useEffect([load]) pair looped on every
   * re-memoise of useNetwork, and this page issues TWO requests per pass, so it
   * was twice as noisy. See GiveawayOfferPage for the full explanation — which
   * also covers why asking with the code, read once at mount, is enough. */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [o, subs] = await Promise.all([
          get(
            offerCode
              ? `/giveaway/offer?c=${encodeURIComponent(offerCode)}`
              : '/giveaway/offer',
            true
          ),
          get('/package/subscription', true)
        ]);
        if (cancelled) return;
        setOffer(o);
        setPacks(Array.isArray(subs) ? subs : [subs]);
      } catch {
        /* The page still reads correctly without either: the copy never depends
           on a number being present, and the packs section simply does not
           render. */
      }
    })();

    track('giveaway_offer.confirmation_view', { offerSlug });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Winners and the participant count.
   *
   * Its own effect, with a try/catch around EACH request rather than one around
   * both: neither is essential to this page, and a failure in either must not
   * take down the entries figure or the pack list, which are what the visitor
   * actually came for. Both degrade to rendering nothing. */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await get('/giveaway/last-winners?limit=10', true);
        if (!cancelled) setWinners(mapWinners(data));
      } catch {
        /* No winners strip. The carousel renders nothing for an empty list. */
      }

      try {
        const stats = await get('/giveaway/stats', true);
        if (!cancelled) {
          const n = Number(stats?.activeJackpotParticipantCount);
          setParticipants(Number.isFinite(n) ? n : null);
        }
      } catch {
        /* The counter simply does not render. */
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = useMemo(
    () => byPriceAscending(toPackSubscriptionItems(packs)),
    [packs]
  );

  const stackingItems = useMemo(
    () => items.map((i) => ({ title: i.title, entries: i.entries })),
    [items]
  );

  /* The dearest plan and the second-cheapest, dearest first — Relic and Epic on
   * today's ladder — picked by position in the price order, never by title (see
   * `offerPagePacks`).
   *
   * `stackingItems` above deliberately stays the FULL list: the explainer picks
   * its worked example by name (`/relic/i`), falling back to the highest-entry
   * plan, so feeding it everything keeps that example identical to the one
   * `/giveaway-subs` shows.
   *
   * WATCH OUT — SubscriptionUpgradeCards styles each card by where its entry
   * count falls between the MIN and MAX of the list it is handed, and names a
   * muted card by INDEX ("Starter plan" first, "Basic plan" after). Handed this
   * pair, Epic is the floor, so it renders muted, as "Basic plan" because it
   * comes second. The four-card page happens to treat it the same for another
   * reason — on today's ladder 50 entries sits under a fifth of the way from 20
   * to 200 — so a change to the ladder can split the two pages. The gold
   * "Maximum entry" card keys off the package id (`isTopSubscriptionPackage`),
   * not position, so the order does not move it.
   *
   * AND EPIC IS NOT DISCOUNT-ELIGIBLE: the first full-price plan this page sells.
   * Its card, the Elements amount below and the receipt all ask
   * `isDiscountEligibleSubscriptionPackage` and quote the full price, and the
   * server picks any first-month coupon from the package id, not from this page. */
  const displayedPacks = useMemo(() => offerPagePacks(items), [items]);

  /* "See the packs", from the popup. Smooth unless the visitor asked for reduced
   * motion, and focus follows so a keyboard user lands where the eye does.
   *
   * SiteHeader is sticky, but not on this page: the root's `overflowX: hidden`
   * below makes the root the header's scroll container, so here it scrolls
   * away with everything else. The heading is still landed below the
   * header's measured height — a few spare pixels today, and right if that
   * backstop ever goes. */
  const packsHeadingRef = useRef<HTMLHeadingElement>(null);

  const scrollToPacks = () => {
    const heading = packsHeadingRef.current;
    if (!heading) return;
    const headerHeight =
      document.querySelector('header')?.getBoundingClientRect().height ?? 0;
    const reduceMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    window.scrollTo({
      top: heading.getBoundingClientRect().top + window.scrollY - headerHeight - 16,
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
    heading.focus({ preventScroll: true });
  };

  /* The checkout, once a visitor picks a plan; `null` until they do.
   *
   * A null `payload` means "we do not hold their details yet" — the dialog then
   * collects them with RaffleEntryForm before showing the card field. Pack and
   * payload are stored together and built once when the dialog opens rather than
   * derived on every render: the payload reads sessionStorage, and Stripe's
   * <Elements> is keyed on the pack and amount, so an object that changed
   * identity mid-payment would remount the card field under the buyer. */
  const [checkout, setCheckout] = useState<{
    pack: PackItem;
    payload: RafflePaymentPayload | null;
  } | null>(null);

  const openCheckout = (id: string) => {
    track('giveaway_offer.pack_click', { packId: String(id), offerSlug });

    const pack = displayedPacks.find((p) => String(p.id) === String(id));
    if (!pack) return;

    /* Null whenever there is no signup draft: a direct link, a fresh tab, a
     * shared URL, or — most likely for a QR funnel — a mobile tab the browser
     * purged while it was backgrounded.
     *
     * This used to redirect to /giveaway-subs, which read as a dead button: the
     * plan you picked vanished and you were on a different page. The dialog now
     * opens either way and asks for the details itself. */
    setCheckout({
      pack,
      payload: buildGiveawayOfferPaymentPayload({
        selectedPack: pack,
        subscriptionItems: items
      })
    });
  };

  /* The details step handing over to the card step.
   *
   * RaffleEntryForm does NOT set the signup-code bonus the cards advertise
   * ("START WITH n ENTRIES FROM THE GET GO"), so it is attached here — the same
   * thing RafflePage does in its own openSubscriptionCheckout. Without it the
   * buyer is charged for an entry count they were shown and do not receive.
   *
   * The details are also written back as a draft, so picking the other plan
   * afterwards does not ask for everything a second time. */
  const onDetailsReady =
    (pack: PackItem) => (payload: RafflePaymentPayload) => {
      const bonus = startingBonusEntries(pack);

      saveGiveawayOfferDraft({
        firstName: payload.entry.firstName,
        lastName: payload.entry.lastName,
        username: payload.entry.username,
        email: payload.entry.email,
        phone: payload.entry.phone,
        countryIso2: payload.entry.countryIso2,
        countryName: payload.entry.countryName,
        stateIso2: payload.entry.stateIso2,
        stateName: payload.entry.stateName,
        phoneCountryCode: payload.entry.phoneCountryCode,
        agreeToTerms: payload.entry.agreeToTerms,
        eligible: payload.entry.eligible
      });

      setCheckout({
        pack,
        payload: {
          ...payload,
          selectedPack: pack,
          subscriptionItems: items,
          signupCodeBonusEntries: bonus > 0 ? bonus : undefined,
          signupCodeBonusSource: bonus > 0 ? 'giveaway_subs' : undefined,
          signupSource: 'giveaway-offer'
        }
      });
    };

  /* Only render a countdown for a draw that is still ahead of us.
   *
   * GiveawayCountdownTimer has no "closed" state — at zero it sits on
   * 00:00:00:00 forever — so a finished or stale draw would paint a dead
   * all-zeros clock on a page people reach from a printed QR code, which reads
   * as broken rather than as "between draws". Showing nothing is the honest
   * fallback: the offer itself still stands, and the entries are still granted
   * against whichever jackpot draw is Active when the member verifies. */
  const drawDate = useMemo(() => {
    if (!offer?.endDate) return null;
    const d = new Date(offer.endDate);
    if (Number.isNaN(d.getTime())) return null;
    return d.getTime() > Date.now() ? d : null;
  }, [offer?.endDate]);

  /* Bound to a const on purpose, rather than read as `checkout.payload` in the
   * JSX below.
   *
   * TypeScript narrows a const across the callbacks it is captured in; it does
   * NOT narrow a property access on state that way, so `checkout.payload` stayed
   * `RafflePaymentPayload | null` inside the card step and its onPaid handler.
   * Babel strips types without checking them, so that compiled and ran fine —
   * only `tsc` caught it. */
  const checkoutPayload = checkout?.payload ?? null;

  /* No code, no number. Without one the endpoint answers with its default, and
   * that is not an amount we can promise whoever is holding this page — the
   * headline falls back to "Your entries are reserved". The request is still
   * made, for the prize and the draw date. */
  const entries = offerCode ? (offer?.entries ?? null) : null;

  return (
    <Box
      sx={{
        background: PAGE_BG,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        /* A backstop, not the fix. The real cause of the sideways scroll was a
           flex item growing to its min-content (see the hero below); this only
           stops any future wide child from making the whole PAGE scroll. */
        overflowX: 'hidden'
      }}
    >
      <SiteHeader />

      <Box
        sx={{
          /* `minWidth: 0` is a guard, not tidying.
           *
           * This Box is a flex item of the column above, so it carries flexbox's
           * automatic `min-width: auto` — it refuses to shrink below its
           * MIN-CONTENT width. That is exactly how this page came to scroll
           * sideways on a phone: the winners marquee (a 1264px `max-content` row)
           * used to sit here and inflated this item to its own maxWidth of 900 on
           * a 390px screen. A clipping `overflow: hidden` on the marquee does NOT
           * help — it clips what is painted, but the intrinsic width still
           * propagates up.
           *
           * The marquee has since moved down under the video, and the packs
           * section carries the same guard for the same reason. This one stays:
           * any wide child dropped in here would do it again. */
          minWidth: 0,
          width: '100%',
          maxWidth: 900,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 5, md: 8 },
          pb: { xs: 4, md: 6 },
          textAlign: 'center'
        }}
      >
        <Typography
          sx={{ ...kickerSx, fontSize: { xs: 11, md: 12 }, mb: 1.75 }}
        >
          YOU&apos;RE ALMOST IN
        </Typography>

        <Typography
          component="h1"
          sx={{
            fontSize: { xs: 30, md: 46 },
            fontWeight: 900,
            lineHeight: 1.1,
            color: INK,
            mb: 2
          }}
        >
          {entries !== null ? freeEntriesCopy(entries).reserved : 'Your entries are reserved'}
          <br />
          for {formatPrize(offer?.prizeAmount ?? null)}
        </Typography>

        {/* The honest sentence, and the reason this page exists in this shape. */}
        <Typography
          sx={{
            fontSize: { xs: 15.5, md: 17 },
            color: BODY,
            maxWidth: 620,
            mx: 'auto',
            lineHeight: 1.65
          }}
        >
          Check your email and click the verification link.
        </Typography>

        {/* Where the draw is watched (Facebook) and the community is (Discord).
            Below the sentence above, which is what activates the entries and so
            stays directly under the headline; above the countdown. */}
        <Box sx={{ mt: { xs: 3, md: 3.5 } }}>
          <GiveawayOfferSocialLinks page="confirmation" />
        </Box>

        {/* The countdown, or an honest stand-in.
         *
         * This block used to disappear entirely when no draw was ahead of us,
         * which is why the timer looked missing: the local active giveaway ended
         * 2026-08-14. The guard itself stays — GiveawayCountdownTimer has no
         * closed state and sits on 00:00:00:00 forever once a draw lapses, which
         * is exactly what /giveaway paints today and reads as broken. So the slot
         * is always occupied, and it tells the truth when there is nothing to
         * count down to. */}
        <Box sx={{ mt: 4 }}>
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 800,
              letterSpacing: '0.16em',
              color: MUTED,
              mb: 1.5
            }}
          >
            {drawDate ? 'DRAWN IN' : 'NEXT DRAW'}
          </Typography>

          {drawDate ? (
            /* Shared timer. Known issue: it renders only `duration.days`, so a
               draw more than about a month out under-reports. Ours is weekly, so
               it is correct here — but do not reuse this for a long campaign
               without fixing GiveawayCountdownTimer first. */
            <GiveawayCountdownTimer nextGiveaway={drawDate} />
          ) : (
            <Typography
              sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 800, color: INK }}
            >
              Announced soon — your entries roll into the next draw.
            </Typography>
          )}
        </Box>

        {/* How many people are already in the draw these entries go into.
            `activeJackpotParticipantCount` is the same figure /giveaway shows. */}
        {participants !== null && (
          <Box sx={{ mt: { xs: 3, md: 4 }, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ ...panelSx, py: 2, px: 3.5, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: { xs: 30, md: 38 },
                  fontWeight: 950,
                  color: CYAN,
                  lineHeight: 1
                }}
              >
                {participants.toLocaleString('en-AU')}
              </Typography>
              <Typography
                sx={{
                  mt: 0.75,
                  fontSize: 12,
                  fontWeight: 900,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: MUTED
                }}
              >
                Users in the current giveaway
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── more entries: the subscription packs ─────────────────────── */}
      {items.length > 0 && (
        <Box
          sx={{
            /* The same flexbox hazard the hero documents: this is a flex item of
               the page column, so `min-width: auto` would let the winners
               marquee's 1264px `max-content` row inflate the whole section on a
               phone. The marquee lives in here now, so the guard does too. */
            minWidth: 0,
            py: { xs: 5, md: 8 },
            borderTop: '1px solid rgba(140,170,190,0.12)'
          }}
        >
          <Box sx={{ maxWidth: 1840, mx: 'auto', px: { xs: 2, md: 4 } }}>
            <Typography
              ref={packsHeadingRef}
              component="h2"
              /* Focusable from script only, for the popup's "See the packs". */
              tabIndex={-1}
              sx={{
                fontSize: { xs: 24, md: 34 },
                fontWeight: 900,
                color: INK,
                textAlign: 'center',
                outline: 'none',
                /* Was 1.25, with the subscription paragraph underneath carrying
                   the rest of the gap before the cards. That paragraph moved down
                   to introduce the stacking explainer, so the spacing moves here
                   — otherwise the cards ride up under the heading. */
                mb: { xs: 3.5, md: 5 }
              }}
            >
              Want more entries every month?
            </Typography>

            <SubscriptionUpgradeCards
              items={displayedPacks}
              onSelect={openCheckout}
            />

            {/* The winner videos, above the stacking explainer by request, in a
                carousel like /giveaway's. Same two URLs, from the shared util so
                the pages cannot drift onto different videos.

                Every slide carries `minWidth: 0`: they are flex items, and
                without it each would refuse to shrink below the iframe's
                intrinsic width and re-introduce the sideways scroll this page
                just had. The arrows use positive insets at `xs` rather than the
                negative ones /giveaway uses, for the same reason — a negative
                offset on a phone hangs outside the viewport. */}
            <Box
              sx={{
                position: 'relative',
                maxWidth: 980,
                width: '100%',
                mx: 'auto',
                mt: { xs: 4, md: 6 }
              }}
            >
              <Box sx={{ overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    transform: `translateX(-${videoIndex * 100}%)`,
                    transition: 'transform 280ms ease'
                  }}
                >
                  {WINNER_VIDEO_URLS.map((url, i) => (
                    <Box key={url} sx={{ flex: '0 0 100%', minWidth: 0 }}>
                      <YouTubeEmbed
                        url={url}
                        title={`Incrypt winner video ${i + 1}`}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>

              {WINNER_VIDEO_URLS.length > 1 && (
                <>
                  {[
                    { dir: -1, Icon: ChevronLeftIcon, side: 'left' as const },
                    { dir: 1, Icon: ChevronRightIcon, side: 'right' as const }
                  ].map(({ dir, Icon, side }) => (
                    <IconButton
                      key={side}
                      aria-label={dir === -1 ? 'Previous video' : 'Next video'}
                      onClick={() =>
                        setVideoIndex(
                          (i) =>
                            (i + dir + WINNER_VIDEO_URLS.length) %
                            WINNER_VIDEO_URLS.length
                        )
                      }
                      sx={{
                        position: 'absolute',
                        top: '42%',
                        /* Inside the video at every width, deliberately.
                           /giveaway hangs its arrows outside on a negative inset,
                           which only works when the viewport is comfortably wider
                           than the 980px track — measured at 1013px the left
                           arrow sat at x=-26 and was half cut off. Overlaying the
                           video edge cannot clip at any width. */
                        [side]: { xs: 2, sm: 8 },
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        width: { xs: 38, sm: 46 },
                        height: { xs: 38, sm: 46 },
                        color: INK,
                        background: 'rgba(9,14,21,0.82)',
                        border: `1px solid ${LINE}`,
                        backdropFilter: 'blur(8px)',
                        '&:hover': {
                          background: 'rgba(9,14,21,0.95)',
                          borderColor: CYAN
                        }
                      }}
                    >
                      <Icon />
                    </IconButton>
                  ))}

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 1,
                      mt: 1
                    }}
                  >
                    {WINNER_VIDEO_URLS.map((url, i) => (
                      <Box
                        key={url}
                        role="button"
                        aria-label={`Show video ${i + 1}`}
                        onClick={() => setVideoIndex(i)}
                        sx={{
                          width: videoIndex === i ? 34 : 10,
                          height: 10,
                          borderRadius: 999,
                          cursor: 'pointer',
                          transition: 'width 180ms ease, background 180ms ease',
                          background:
                            videoIndex === i ? CYAN : 'rgba(140,170,190,0.30)'
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>

            {/* Previous winners, under the video by request. Already dark-styled
                (it is built for the giveaway page's dark hero), so it needs no
                treatment here, and it renders nothing when there are no winners.

                Its 1264px `max-content` row is why the section wrapper above
                carries `minWidth: 0` — see the comment there. */}
            {winners.length > 0 && (
              <Box sx={{ mt: { xs: 4, md: 6 } }}>
                <Typography
                  sx={{
                    ...kickerSx,
                    fontSize: 11,
                    mb: 1.5,
                    textAlign: 'center'
                  }}
                >
                  PREVIOUS WINNERS
                </Typography>
                <RaffleWinnersCarousel winners={winners} />
              </Box>
            )}

            {/* The subscription line, moved down to introduce the stacking
                explainer rather than the section as a whole. */}
            <Typography
              sx={{
                fontSize: { xs: 15, md: 16.5 },
                color: BODY,
                textAlign: 'center',
                maxWidth: 680,
                mx: 'auto',
                mt: { xs: 5, md: 7 },
                mb: { xs: 2.5, md: 3 },
                lineHeight: 1.65
              }}
            >
              A subscription adds entries to every draw and they stack the longer
              you stay — plus Digital Collectibles and INC Token each month.
            </Typography>

            {/* Stacking explainer, below the packs by request. */}
            <SubscriptionStackingExplainer items={stackingItems} />
          </Box>
        </Box>
      )}

      {/* ── the four steps ───────────────────────────────────────────── */}
      {/* Below the packs by request. The full-width wrapper carries the rule and
          the vertical rhythm so this section is separated from the packs the same
          way the packs are separated from the hero. */}
      <Box
        sx={{
          py: { xs: 5, md: 8 },
          borderTop: '1px solid rgba(140,170,190,0.12)'
        }}
      >
        <Box sx={{ maxWidth: 1040, mx: 'auto', px: { xs: 2.5, md: 4 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
              gap: { xs: 2, md: 2.5 }
            }}
          >
            {processSteps.map((step, i) => (
              <Box
                key={step.title}
                sx={{ ...panelSx, p: 2.5, textAlign: 'left' }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing: '0.14em',
                    color: i === 1 ? CYAN : MUTED,
                    mb: 1
                  }}
                >
                  STEP {i + 1}
                </Typography>
                <Typography sx={{ fontSize: 16.5, fontWeight: 800, color: INK, mb: 0.75 }}>
                  {step.title}
                </Typography>
                <Typography sx={{ fontSize: 14, color: BODY, lineHeight: 1.6 }}>
                  {step.copy}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── "want more entries?" — points at the packs, once per arrival ── */}
      <GiveawayOfferMoreEntriesDialog
        upTo={largestStartingBonus(displayedPacks)}
        checkoutOpen={checkout !== null}
        onSeePacks={scrollToPacks}
      />

      {/* ── the subscription checkout ─────────────────────────────────── */}
      {checkout && (
        <Dialog
          open
          fullScreen={isSmallScreen}
          maxWidth={false}
          onClose={(event, reason) => {
            /* A card form is not something to close by a stray click. Same rule
               as the other two checkout dialogs in this funnel. */
            if (reason === 'backdropClick') return;
            if (reason === 'escapeKeyDown') return;
            setCheckout(null);
          }}
          PaperProps={{
            sx: {
              width: isSmallScreen ? '100%' : '50vw',
              maxWidth: isSmallScreen ? '100%' : 760,
              borderRadius: isSmallScreen ? 0 : 4,
              /* Dark, to match the rest of this funnel. The other two checkouts
                 in the codebase are white because they sit on the light giveaway
                 pages; this one sits on `giveawayOfferTheme`'s dark ground, so
                 the dialog, Stripe's own fields and the form chrome all have to
                 be darkened together or the seams show. */
              background: PAGE_BG,
              border: `1px solid ${LINE}`,
              boxShadow: '0 24px 64px rgba(0,0,0,0.55)'
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${LINE}`
            }}
          >
            <Typography sx={{ fontWeight: 950, color: INK, fontSize: 16 }}>
              {checkoutPayload ? 'Checkout' : 'Your details'}
            </Typography>
            <Box sx={{ ml: 'auto' }}>
              <IconButton
                onClick={() => setCheckout(null)}
                sx={{ color: MUTED, '&:hover': { color: INK } }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {!checkoutPayload && (
              /* No draft — collect the details first, then fall through to the
                 card step. The same form /giveaway-subs uses, so there is one
                 implementation of these fields and their validation. It creates
                 no account and probes no address; the server still identifies
                 the member by email when the payment is made. */
              <RaffleEntryForm
                packageName={checkout.pack.title}
                surface="dark"
                selectedPack={checkout.pack}
                standaloneItems={[]}
                subscriptionItems={items}
                signupSource="giveaway-offer"
                /* The sign-up's draft has the name, username and email already
                   (and, since 2026-10-07, nothing else): typed in, not asked twice. */
                initialEntry={draftEntryPrefill(getGiveawayOfferDraft())}
                onReadyToPay={onDetailsReady(checkout.pack)}
              />
            )}

            {checkoutPayload && (
            <Elements
              key={`giveaway-offer:${checkout.pack.id}:${Math.round(
                displayedSubscriptionPrice(checkout.pack) * 100
              )}`}
              stripe={stripePromise}
              options={{
                mode: 'subscription',
                currency: 'aud',
                paymentMethodTypes: ['card'],
                setupFutureUsage: 'off_session',
                amount: Math.round(
                  displayedSubscriptionPrice(checkout.pack) * 100
                ),
                appearance: {
                  /* Stripe renders its card fields inside an iframe that our CSS
                     cannot reach, so the dark treatment has to be declared here
                     rather than styled from the page. Values come from
                     giveawayOfferTheme so the field matches the panel around it. */
                  theme: 'night',
                  variables: {
                    fontSizeBase: '16px',
                    colorPrimary: CYAN,
                    colorBackground: '#0d141d',
                    colorText: INK,
                    colorTextSecondary: MUTED,
                    colorDanger: '#ff8b8b',
                    borderRadius: '10px'
                  },
                  rules: {
                    '.Label': { color: MUTED, fontWeight: '700' },
                    '.Input': { borderColor: 'rgba(140,170,190,0.22)' },
                    '.Input:focus': { borderColor: CYAN, boxShadow: 'none' }
                  }
                }
              }}
            >
              <RaffleStripeCheckoutForm
                payload={checkoutPayload}
                surface="dark"
                mode="subscription"
                pack={checkout.pack}
                subDiscount={SUBSCRIPTION_DISCOUNT_PERCENT}
                discountAllSubscriptionPacks={isDiscountEligibleSubscriptionPackage(
                  checkout.pack.id
                )}
                onPaid={({ paymentIntentId, accountCreated }) => {
                  saveGiveawayOfferConfirmationDraft({
                    payload: checkoutPayload,
                    pack: checkout.pack,
                    paymentIntentId,
                    accountCreated
                  });
                  setCheckout(null);
                  /* This funnel's OWN dark confirmation screen, not
                     /giveaway-subs/confirmation: that one is shared by three
                     routes and styled for the light giveaway pages. It reads the
                     same `giveawayConfirmationState` contract written just above.
                     The free entries are granted server-side off the webhook,
                     not here; this navigation only shows the receipt. */
                  navigate(`/${offerSlug}/subscribed`, { replace: true });
                }}
              />
            </Elements>
            )}
          </Box>
        </Dialog>
      )}

      <RaffleFooter />
    </Box>
  );
};

/* Only for a browser that has just come through the sign-up.
 *
 * Without this, anyone with the URL was told "N entries are reserved" without
 * having signed up — untrue, and a way past the form. It is a COURTESY GATE, NOT
 * SECURITY: nothing here grants anything (entries are granted server-side, and
 * only to an account that signed up with a live code); it stops this page from
 * stating something false.
 *
 * TWO PROOFS, EITHER WILL DO. The router state the offer page navigates here
 * with (`signedUp`) survives a reload of that history entry and needs no
 * storage, but a payment redirect is a fresh page load and loses it. The signup
 * draft in sessionStorage covers that return (and the checkout's details step,
 * which writes one), but it is lost wherever storage is blocked — and that
 * visitor would be bounced straight back to the form after signing up. With
 * neither, the visitor goes to the offer page for the same link and code, which
 * shows the form for a live code and the right refusal otherwise.
 *
 * A wrapper rather than an early return, so the page's hooks — its requests and
 * its view event — never run for a visitor who is being sent away. */
const GiveawayOfferConfirmationPage = () => {
  const location = useLocation();
  const offerSlug = useOfferSlug();
  const [searchParams] = useSearchParams();
  const offerCode = (searchParams.get('c') ?? '').trim().toUpperCase();

  const cameThroughSignUp =
    (location.state as { signedUp?: boolean } | null)?.signedUp === true ||
    getGiveawayOfferDraft() !== null;

  if (!cameThroughSignUp) {
    return (
      <Navigate
        replace
        to={
          offerCode
            ? `/${offerSlug}?c=${encodeURIComponent(offerCode)}`
            : `/${offerSlug}`
        }
      />
    );
  }

  return <ConfirmationPageContent />;
};

export default GiveawayOfferConfirmationPage;
